import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Building2, Mail, Phone, User, ArrowLeft, FolderKanban, Ticket as TicketIcon,
  FileText, Wallet, Clock, Send, Save, Loader2, Calendar, Euro, 
  CheckCircle, MessageSquare, Plus, RefreshCw, Sparkles, CreditCard, Receipt, 
  FileSignature, MapPin, Hash, Globe, ChevronRight, Flame, ExternalLink,
  TrendingUp, PhoneCall, MessageCircle, Users, Pencil, Key, Trash2,
  Timer, UserCheck, Briefcase, Award, Tag, UserPlus, Upload, Image as ImageIcon,
  Facebook, Instagram, Linkedin, Twitter, Youtube, Link as LinkIcon,
  AlertTriangle, CalendarDays, Repeat, History, Eye, FileUp, Building,
  Settings, Layers, DollarSign, Activity
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

// Tags disponibles
const AVAILABLE_TAGS = [
  { id: 'vip', label: 'VIP', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'prospect_chaud', label: 'Prospect chaud', color: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'fidele', label: 'Fidèle', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'a_relancer', label: 'À relancer', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { id: 'site_agence', label: 'Site agence', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { id: 'site_concurrent', label: 'Site concurrent', color: 'bg-rose-100 text-rose-700 border-rose-300' },
  { id: 'recurrent', label: 'Revenu récurrent', color: 'bg-green-100 text-green-700 border-green-300' },
];

const ClientDetailPageV2 = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Main Tab
  const [mainTab, setMainTab] = useState('overview');
  
  // Data
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [clientStats, setClientStats] = useState(null);
  const [schedules, setSchedules] = useState([]);
  
  // CA
  const [caStats, setCaStats] = useState({ lastYear: 0, thisYear: 0, thisMonth: 0 });
  const [caChartData, setCaChartData] = useState([]);
  
  // AI
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  
  // Contacts
  const [contacts, setContacts] = useState([]);
  const [clientTags, setClientTags] = useState([]);
  
  // Modals
  const [showEditClient, setShowEditClient] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', function: '', email: '', phone: '' });
  
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', message: '', priority: 'normal', project_id: '' });
  
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [exchangeForm, setExchangeForm] = useState({ type: 'phone', subject: '', content: '', date: new Date().toISOString().slice(0, 16) });

  useEffect(() => {
    if (clientId) fetchAllData();
  }, [clientId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [clientRes, projectsRes, ticketsRes, contractsRes, walletRes, receiptsRes, timelineRes, statsRes, schedulesRes] = await Promise.all([
        api.get(`/users/${clientId}`),
        api.get('/projects/', { params: { client_id: clientId } }),
        api.get('/tickets/', { params: { client_id: clientId } }),
        api.get('/contracts/', { params: { client_id: clientId } }),
        api.get(`/wallet/user/${clientId}`).catch(() => ({ data: null })),
        api.get('/receipts/', { params: { client_id: clientId } }).catch(() => ({ data: [] })),
        api.get(`/users/clients/${clientId}/activity-timeline`).catch(() => ({ data: { timeline: [] } })),
        api.get(`/users/clients/${clientId}/stats`).catch(() => ({ data: null })),
        api.get('/payment-schedules/', { params: { client_id: clientId } }).catch(() => ({ data: [] }))
      ]);
      
      setClient(clientRes.data);
      setEditData(clientRes.data);
      setClientTags(clientRes.data?.tags || []);
      setContacts(clientRes.data?.additional_contacts || []);
      setProjects(projectsRes.data || []);
      setTickets(ticketsRes.data || []);
      setContracts(contractsRes.data || []);
      setWallet(walletRes.data);
      setReceipts(receiptsRes.data || []);
      setTimeline(timelineRes.data?.timeline || []);
      setClientStats(statsRes.data);
      setSchedules(schedulesRes.data || []);
      calculateCA(receiptsRes.data || []);
      if (clientRes.data?.ai_summary) setAiSummary(clientRes.data.ai_summary);
    } catch (error) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const calculateCA = (data) => {
    const now = new Date();
    let lastYear = 0, thisYear = 0, thisMonth = 0;
    
    // Monthly data for chart (last 12 months)
    const monthlyData = {};
    const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    
    // Initialize last 12 months
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthlyData[key] = { month: monthNames[d.getMonth()], ca: 0, year: d.getFullYear() };
    }
    
    data.forEach(r => {
      const amount = r.amount_ttc || 0;
      const date = new Date(r.created_at);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      
      if (monthlyData[key]) monthlyData[key].ca += amount;
      if (date.getFullYear() === now.getFullYear() - 1) lastYear += amount;
      if (date.getFullYear() === now.getFullYear()) {
        thisYear += amount;
        if (date.getMonth() === now.getMonth()) thisMonth += amount;
      }
    });
    
    setCaStats({ lastYear, thisYear, thisMonth });
    setCaChartData(Object.values(monthlyData));
  };

  const generateAiSummary = async () => {
    setLoadingAi(true);
    try {
      const res = await api.get(`/users/clients/${clientId}/ai-summary`);
      setAiSummary(res.data.summary);
      toast.success('Résumé généré !');
    } catch (e) { toast.error('Erreur IA'); }
    finally { setLoadingAi(false); }
  };

  const saveClient = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${clientId}`, { ...editData, tags: clientTags });
      setClient({ ...client, ...editData, tags: clientTags });
      setShowEditClient(false);
      toast.success('Enregistré !');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const saveContact = async () => {
    setSaving(true);
    try {
      let updated;
      if (editingContact) {
        updated = contacts.map(c => c.id === editingContact.id ? { ...contactForm, id: editingContact.id } : c);
      } else {
        updated = [...contacts, { ...contactForm, id: Date.now().toString() }];
      }
      await api.put(`/users/${clientId}`, { ...client, additional_contacts: updated });
      setContacts(updated);
      setShowAddContact(false);
      setEditingContact(null);
      setContactForm({ first_name: '', last_name: '', function: '', email: '', phone: '' });
      toast.success('Contact enregistré !');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const deleteContact = async (id) => {
    const updated = contacts.filter(c => c.id !== id);
    await api.put(`/users/${clientId}`, { ...client, additional_contacts: updated });
    setContacts(updated);
    toast.success('Contact supprimé');
  };

  const createTicket = async () => {
    setSaving(true);
    try {
      const res = await api.post('/tickets/', { 
        subject: ticketForm.subject, 
        message: ticketForm.message, 
        priority: ticketForm.priority,
        client_id: clientId,
        project_id: ticketForm.project_id || null
      });
      setTickets([res.data, ...tickets]);
      setShowNewTicket(false);
      setTicketForm({ subject: '', message: '', priority: 'normal', project_id: '' });
      toast.success('Ticket créé !');
    } catch (e) { toast.error('Erreur création ticket'); }
    finally { setSaving(false); }
  };

  const saveExchange = async () => {
    setSaving(true);
    try {
      await api.post(`/users/clients/${clientId}/notes`, { ...exchangeForm, created_by: user.id });
      const res = await api.get(`/users/clients/${clientId}/activity-timeline`);
      setTimeline(res.data?.timeline || []);
      setShowExchangeModal(false);
      setExchangeForm({ type: 'phone', subject: '', content: '', date: new Date().toISOString().slice(0, 16) });
      toast.success('Échange enregistré !');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const sendResetPassword = async () => {
    try {
      await api.post('/auth/forgot-password', { email: client.email });
      toast.success(`Lien envoyé à ${client.email}`);
    } catch (e) { toast.error('Erreur'); }
  };

  const toggleTag = (id) => setClientTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

  // Computed
  const clientName = client?.company_name || `${client?.first_name || ''} ${client?.last_name || ''}`.trim() || client?.email;
  const openTickets = tickets.filter(t => !['closed', 'resolved'].includes(t.status));
  const activeProjects = projects.filter(p => !['completed', 'cancelled'].includes(p.status));
  const pendingBriefs = projects.filter(p => p.status === 'brief_pending' || p.brief_status === 'pending');
  const pendingContracts = contracts.filter(c => c.status === 'pending' || c.status === 'awaiting_signature');
  const walletBalance = wallet?.balance_hours || 0;
  const monthlyRevenue = contracts.filter(c => c.is_recurring && c.status === 'active').reduce((sum, c) => sum + (c.monthly_amount || 0), 0);
  const renewalDates = contracts.filter(c => c.renewal_date).sort((a, b) => new Date(a.renewal_date) - new Date(b.renewal_date));

  if (loading) return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!client) return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center"><p>Client non trouvé</p></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/30">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-3 md:px-4 pb-4", collapsed ? "md:ml-20" : "md:ml-64")} style={{ paddingTop: "90px" }}>
        
        {/* Header compact */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/clients')} size="sm" className="h-9 rounded-full hover:bg-white/80">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Clients
          </Button>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-9 rounded-full text-xs border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300" onClick={() => setShowExchangeModal(true)}>
              <PhoneCall className="w-3.5 h-3.5 mr-1.5 text-indigo-500" />Échange
            </Button>
            <Button size="sm" variant="outline" className="h-9 rounded-full text-xs border-amber-200 hover:bg-amber-50 hover:border-amber-300" onClick={() => setShowNewTicket(true)}>
              <TicketIcon className="w-3.5 h-3.5 mr-1.5 text-amber-500" />Ticket
            </Button>
            <Button size="sm" variant="outline" className="h-9 rounded-full text-xs border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300" asChild>
              <Link to={`/contracts/new?client=${clientId}`}><FileSignature className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />Contrat</Link>
            </Button>
            <Button size="sm" className="h-9 rounded-full text-xs bg-indigo-500 hover:bg-indigo-600 shadow-sm" onClick={() => setShowEditClient(true)}>
              <Pencil className="w-3.5 h-3.5 mr-1.5" />Modifier
            </Button>
          </div>
        </div>

        {/* Client Header Card */}
        <Card className="card mb-3">
          <CardContent className="p-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              {/* Client Info */}
              <div className="flex items-center gap-3">
                {/* Logo ou Avatar */}
                {client.company_logo_url ? (
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white border shadow-sm flex-shrink-0 overflow-hidden p-1">
                    <img 
                      src={client.company_logo_url.startsWith('/') 
                        ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${client.company_logo_url}` 
                        : client.company_logo_url
                      }
                      alt={clientName}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0 shadow-sm"
                    style={{ background: `linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)` }}>
                    {(clientName || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-bold text-xl text-slate-800">{clientName}</h1>
                    {clientTags.slice(0, 3).map(id => {
                      const tag = AVAILABLE_TAGS.find(t => t.id === id);
                      return tag ? <Badge key={id} variant="outline" className={cn("text-[10px] rounded-full px-2", tag.color)}>{tag.label}</Badge> : null;
                    })}
                    <Button size="icon" variant="ghost" className="w-6 h-6 rounded-full hover:bg-indigo-50" onClick={() => setShowTagsModal(true)}><Tag className="w-3.5 h-3.5 text-indigo-500" /></Button>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{client.activity_description || client.sector || 'Activité non renseignée'}</p>
                  {/* Date création + MAJ + Social Links */}
                  <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                    {client.created_at && (
                      <span className="text-[11px] text-slate-400 flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded-full">
                        <Calendar className="w-3 h-3" />Depuis {new Date(client.created_at).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {client.updated_at && (
                      <span className="text-[11px] text-slate-400 flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full" title={`Dernière modification: ${new Date(client.updated_at).toLocaleString('fr-FR')}`}>
                        <RefreshCw className="w-3 h-3" />MAJ {new Date(client.updated_at).toLocaleDateString('fr-FR')}
                        {client.updated_by_name && <span className="text-amber-600">par {client.updated_by_name}</span>}
                      </span>
                    )}
                    <div className="flex items-center gap-1.5">
                      {client.website && <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-indigo-100 hover:text-indigo-600 transition-colors" title={client.website}><Globe className="w-3.5 h-3.5" /></a>}
                      {client.facebook_url && <a href={client.facebook_url} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 hover:bg-blue-100 transition-colors" title="Facebook"><Facebook className="w-3.5 h-3.5" /></a>}
                      {client.instagram_url && <a href={client.instagram_url} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-pink-50 flex items-center justify-center text-pink-500 hover:bg-pink-100 transition-colors" title="Instagram"><Instagram className="w-3.5 h-3.5" /></a>}
                      {client.linkedin_url && <a href={client.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-sky-50 flex items-center justify-center text-sky-600 hover:bg-sky-100 transition-colors" title="LinkedIn"><Linkedin className="w-3.5 h-3.5" /></a>}
                      {client.twitter_url && <a href={client.twitter_url} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors" title="Twitter/X"><Twitter className="w-3.5 h-3.5" /></a>}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* CA Stats - Design pastel */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-center px-4 py-2 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] text-slate-400 font-medium">CA {new Date().getFullYear() - 1}</p>
                  <p className="text-lg font-bold text-slate-600">{caStats.lastYear.toLocaleString('fr-FR')}€</p>
                </div>
                <div className="text-center px-4 py-2 bg-emerald-50 rounded-2xl">
                  <p className="text-[10px] text-emerald-600 font-medium">CA {new Date().getFullYear()}</p>
                  <p className="text-lg font-bold text-emerald-600">{caStats.thisYear.toLocaleString('fr-FR')}€</p>
                </div>
                <div className="text-center px-4 py-2 bg-indigo-50 rounded-2xl">
                  <p className="text-[10px] text-indigo-600 font-medium">Ce mois</p>
                  <p className="text-lg font-bold text-indigo-600">{caStats.thisMonth.toLocaleString('fr-FR')}€</p>
                </div>
                {monthlyRevenue > 0 && (
                  <div className="text-center bg-green-50 px-3 py-1 rounded-lg">
                    <p className="text-[10px] text-green-600"><Repeat className="w-3 h-3 inline mr-1" />Récurrent</p>
                    <p className="text-lg font-bold text-green-600">{monthlyRevenue}€/mois</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-3">
          <TabsList className="w-full grid grid-cols-6 h-9">
            <TabsTrigger value="overview" className="text-xs"><Activity className="w-3 h-3 mr-1 hidden md:inline" />Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="contacts" className="text-xs"><Users className="w-3 h-3 mr-1 hidden md:inline" />Contacts</TabsTrigger>
            <TabsTrigger value="commercial" className="text-xs"><Building className="w-3 h-3 mr-1 hidden md:inline" />Commercial</TabsTrigger>
            <TabsTrigger value="projects" className="text-xs"><FolderKanban className="w-3 h-3 mr-1 hidden md:inline" />Projets</TabsTrigger>
            <TabsTrigger value="finance" className="text-xs"><Wallet className="w-3 h-3 mr-1 hidden md:inline" />Finance</TabsTrigger>
            <TabsTrigger value="docs" className="text-xs"><FileText className="w-3 h-3 mr-1 hidden md:inline" />Documents</TabsTrigger>
          </TabsList>

          {/* ========== VUE D'ENSEMBLE ========== */}
          <TabsContent value="overview" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Alertes & En attente */}
              {(pendingBriefs.length > 0 || pendingContracts.length > 0 || openTickets.length > 0) && (
                <Card className="card lg:col-span-3 border-l-4 border-l-amber-500">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="font-medium text-sm">Éléments en attente</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pendingBriefs.length > 0 && <Badge className="bg-purple-100 text-purple-700">{pendingBriefs.length} Brief(s) en attente</Badge>}
                      {pendingContracts.length > 0 && <Badge className="bg-indigo-100 text-indigo-700">{pendingContracts.length} Contrat(s) à signer</Badge>}
                      {openTickets.length > 0 && <Badge className="bg-amber-100 text-amber-700">{openTickets.length} Ticket(s) ouvert(s)</Badge>}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Résumé IA */}
              <Card className="card lg:col-span-2">
                <CardHeader className="pb-2 p-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-500" />Résumé IA</CardTitle>
                    <Button size="sm" variant="ghost" onClick={generateAiSummary} disabled={loadingAi} className="h-7 text-xs">
                      {loadingAi ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {loadingAi ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-purple-500" /></div>
                  ) : aiSummary ? (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{aiSummary}</p>
                  ) : (
                    <div className="text-center py-6">
                      <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <Button size="sm" onClick={generateAiSummary}>Générer le résumé</Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stats rapides */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm">Statistiques</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-2">
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-xs text-slate-600"><FolderKanban className="w-3 h-3 inline mr-1" />Projets actifs</span>
                    <span className="font-bold text-sm">{activeProjects.length}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-xs text-slate-600"><TicketIcon className="w-3 h-3 inline mr-1" />Tickets ouverts</span>
                    <span className="font-bold text-sm">{openTickets.length}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-xs text-slate-600"><Wallet className="w-3 h-3 inline mr-1" />Wallet</span>
                    <span className="font-bold text-sm" style={{ color: walletBalance >= 0 ? '#16a34a' : '#dc2626' }}>{walletBalance.toFixed(1)}h</span>
                  </div>
                  {clientStats?.ticket_stats?.avg_response_time_hours && (
                    <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                      <span className="text-xs text-blue-600"><Timer className="w-3 h-3 inline mr-1" />Temps rép. moy.</span>
                      <span className="font-bold text-sm text-blue-600">{clientStats.ticket_stats.avg_response_time_hours}h</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Historique des échanges */}
              <Card className="card lg:col-span-3">
                <CardHeader className="pb-2 p-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" />Historique des échanges</CardTitle>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowExchangeModal(true)}>
                      <Plus className="w-3 h-3 mr-1" />Saisir
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <ScrollArea className="h-[250px]">
                    {timeline.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Aucun échange enregistré</p>
                    ) : (
                      <div className="space-y-2">
                        {timeline.slice(0, 20).map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-2 rounded hover:bg-slate-50">
                            <div className={cn("w-8 h-8 rounded flex items-center justify-center flex-shrink-0",
                              item.type === 'message' ? 'bg-blue-100 text-blue-600' :
                              item.type === 'phone' ? 'bg-cyan-100 text-cyan-600' :
                              item.type === 'email' ? 'bg-pink-100 text-pink-600' :
                              item.type === 'payment' ? 'bg-emerald-100 text-emerald-600' :
                              'bg-slate-100 text-slate-600'
                            )}>
                              {item.type === 'message' ? <MessageSquare className="w-4 h-4" /> :
                               item.type === 'phone' ? <PhoneCall className="w-4 h-4" /> :
                               item.type === 'email' ? <Mail className="w-4 h-4" /> :
                               item.type === 'payment' ? <CreditCard className="w-4 h-4" /> :
                               <Clock className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start">
                                <p className="text-sm font-medium">{item.title}</p>
                                <span className="text-[10px] text-slate-400">{item.date ? new Date(item.date).toLocaleDateString('fr-FR') : ''}</span>
                              </div>
                              <p className="text-xs text-slate-500 truncate">{item.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== CONTACTS ========== */}
          <TabsContent value="contacts" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Contact principal */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Contact principal</CardTitle>
                    <Badge variant="outline" className="text-[10px]">Principal</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-2 text-sm">
                  <p><User className="w-4 h-4 inline mr-2 text-slate-400" />{client.first_name} {client.last_name}</p>
                  {client.function && <p><Building2 className="w-4 h-4 inline mr-2 text-slate-400" />{client.function}</p>}
                  <p><Mail className="w-4 h-4 inline mr-2 text-slate-400" /><a href={`mailto:${client.email}`} className="hover:underline">{client.email}</a></p>
                  {client.phone && <p><Phone className="w-4 h-4 inline mr-2 text-slate-400" /><a href={`tel:${client.phone}`}>{client.phone}</a></p>}
                  {client.address && <p><MapPin className="w-4 h-4 inline mr-2 text-slate-400" />{client.address} {client.postal_code} {client.city}</p>}
                  <div className="pt-2 border-t flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1 text-xs" asChild><Link to={`/messages?user=${clientId}`}><MessageCircle className="w-3 h-3 mr-1" />Chat</Link></Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={sendResetPassword}><Key className="w-3 h-3 mr-1" />Reset MDP</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Responsable compte */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><UserCheck className="w-4 h-4" />Responsable de compte</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {clientStats?.account_manager ? (
                    <div className="flex items-center gap-3 p-2 bg-indigo-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold">
                        {(clientStats.account_manager.name || 'A')[0]}
                      </div>
                      <div>
                        <p className="font-medium">{clientStats.account_manager.name}</p>
                        <p className="text-xs text-slate-500">{clientStats.account_manager.email}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Non assigné</p>
                  )}
                </CardContent>
              </Card>

              {/* Autres contacts */}
              <Card className="card lg:col-span-2">
                <CardHeader className="pb-2 p-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Contacts ({contacts.length + 1})</CardTitle>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingContact(null); setContactForm({ first_name: '', last_name: '', function: '', email: '', phone: '' }); setShowAddContact(true); }}>
                      <UserPlus className="w-3 h-3 mr-1" />Ajouter
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {contacts.map(c => (
                      <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-slate-50 group">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">
                          {(c.first_name || 'C')[0]}{(c.last_name || '')[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
                          <p className="text-[10px] text-slate-500 truncate">{c.function || c.email}</p>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                          <Button size="icon" variant="ghost" className="w-6 h-6" onClick={() => { setEditingContact(c); setContactForm(c); setShowAddContact(true); }}><Pencil className="w-3 h-3" /></Button>
                          <Button size="icon" variant="ghost" className="w-6 h-6" onClick={() => deleteContact(c.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Équipe ayant travaillé */}
              {clientStats?.team_members?.length > 0 && (
                <Card className="card lg:col-span-2">
                  <CardHeader className="pb-2 p-3">
                    <CardTitle className="text-sm flex items-center gap-2"><Briefcase className="w-4 h-4" />Membres ayant travaillé ({clientStats.team_members.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {clientStats.team_members.map(m => (
                        <Link key={m.id} to={`/team/${m.id}`} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-slate-50">
                          <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">{(m.name || 'M')[0]}</div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium truncate">{m.name}</p>
                            <Badge variant="outline" className="text-[9px]"><Award className="w-2 h-2 mr-0.5" />{m.projects_count} proj.</Badge>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ========== COMMERCIAL ========== */}
          <TabsContent value="commercial" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Situation site web */}
              <Card className="card lg:col-span-2">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Globe className="w-4 h-4" />Situation site web</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: client.has_website_with_us ? '#dcfce7' : client.has_website_with_competitor ? '#fee2e2' : '#f1f5f9' }}>
                    <span className="text-sm font-medium">
                      {client.has_website_with_us ? '✅ Site géré par notre agence' : 
                       client.has_website_with_competitor ? '⚠️ Site géré par un concurrent' : 
                       client.website ? '🌐 A un site web (prestataire inconnu)' :
                       '❌ Pas de site web'}
                    </span>
                    {client.website && (
                      <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline text-sm flex items-center gap-1">
                        {client.website}<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  
                  {/* Site avec nous - détails */}
                  {client.has_website_with_us && (
                    <div className="p-3 bg-emerald-50 rounded-lg space-y-2">
                      <p className="text-sm font-medium text-emerald-700">Site géré par MSC L'Agence</p>
                      {client.website && <p className="text-sm"><Globe className="w-4 h-4 inline mr-2 text-emerald-600" />URL: <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">{client.website}</a></p>}
                      {client.hosting_provider && <p className="text-sm"><Settings className="w-4 h-4 inline mr-2 text-emerald-600" />Hébergeur: <strong>{client.hosting_provider}</strong></p>}
                      {client.domain_registrar && <p className="text-sm"><Globe className="w-4 h-4 inline mr-2 text-emerald-600" />Registrar domaine: <strong>{client.domain_registrar}</strong></p>}
                      {client.website_renewal_date && <p className="text-sm"><CalendarDays className="w-4 h-4 inline mr-2 text-emerald-600" />Renouvellement: <strong>{new Date(client.website_renewal_date).toLocaleDateString('fr-FR')}</strong></p>}
                    </div>
                  )}
                  
                  {/* Site chez concurrent - détails */}
                  {client.has_website_with_competitor && (
                    <div className="p-3 bg-rose-50 rounded-lg space-y-2">
                      <p className="text-sm font-medium text-rose-700">Informations sur le concurrent</p>
                      {client.competitor_name && <p className="text-sm"><Building className="w-4 h-4 inline mr-2 text-rose-600" />Concurrent: <strong>{client.competitor_name}</strong></p>}
                      {client.competitor_contract_end_date && <p className="text-sm"><CalendarDays className="w-4 h-4 inline mr-2 text-rose-600" />Fin engagement: <strong>{new Date(client.competitor_contract_end_date).toLocaleDateString('fr-FR')}</strong></p>}
                      {client.competitor_monthly_amount && <p className="text-sm"><Euro className="w-4 h-4 inline mr-2 text-rose-600" />Montant mensuel: <strong>{client.competitor_monthly_amount}€/mois</strong></p>}
                      {client.competitor_contract_url && (
                        <a href={client.competitor_contract_url} target="_blank" rel="noopener noreferrer" className="text-sm text-rose-600 hover:underline flex items-center">
                          <FileUp className="w-4 h-4 mr-2" />Voir le contrat concurrent
                        </a>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Réseaux sociaux */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" />Réseaux sociaux</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-2">
                  {client.facebook_url || client.instagram_url || client.linkedin_url || client.twitter_url || client.youtube_url ? (
                    <>
                      {client.facebook_url && (
                        <a href={client.facebook_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-sm">
                          <Facebook className="w-5 h-5 text-blue-600" />
                          <span className="flex-1 truncate">{client.facebook_url.replace(/https?:\/\/(www\.)?facebook\.com\//i, '')}</span>
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </a>
                      )}
                      {client.instagram_url && (
                        <a href={client.instagram_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-sm">
                          <Instagram className="w-5 h-5 text-pink-500" />
                          <span className="flex-1 truncate">{client.instagram_url.replace(/https?:\/\/(www\.)?instagram\.com\//i, '@')}</span>
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </a>
                      )}
                      {client.linkedin_url && (
                        <a href={client.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-sm">
                          <Linkedin className="w-5 h-5 text-blue-700" />
                          <span className="flex-1 truncate">LinkedIn</span>
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </a>
                      )}
                      {client.twitter_url && (
                        <a href={client.twitter_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-sm">
                          <Twitter className="w-5 h-5 text-sky-500" />
                          <span className="flex-1 truncate">{client.twitter_url.replace(/https?:\/\/(www\.)?(twitter|x)\.com\//i, '@')}</span>
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </a>
                      )}
                      {client.youtube_url && (
                        <a href={client.youtube_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-sm">
                          <Youtube className="w-5 h-5 text-red-600" />
                          <span className="flex-1 truncate">YouTube</span>
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </a>
                      )}
                    </>
                  ) : (
                    <p className="text-center text-slate-500 py-4">Aucun réseau social renseigné</p>
                  )}
                </CardContent>
              </Card>

              {/* Revenus récurrents */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Repeat className="w-4 h-4" />Revenus récurrents</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {monthlyRevenue > 0 ? (
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">{monthlyRevenue}€</p>
                      <p className="text-sm text-green-600">par mois</p>
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">Aucun revenu récurrent</p>
                  )}
                </CardContent>
              </Card>

              {/* Dates de renouvellement */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><CalendarDays className="w-4 h-4" />Dates de renouvellement</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {renewalDates.length > 0 ? (
                    <div className="space-y-2">
                      {renewalDates.map(c => (
                        <div key={c.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <span className="text-sm truncate flex-1">{c.title || c.project_name}</span>
                          <Badge variant="outline" className="text-xs">{new Date(c.renewal_date).toLocaleDateString('fr-FR')}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">Aucune date de renouvellement</p>
                  )}
                </CardContent>
              </Card>

              {/* Liens rapides */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><LinkIcon className="w-4 h-4" />Liens & Accès</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-2">
                  {client.supports_com_url && (
                    <a href={client.supports_com_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 text-sm">
                      <Layers className="w-4 h-4 text-indigo-500" />Supports de communication<ExternalLink className="w-3 h-3 ml-auto" />
                    </a>
                  )}
                  {client.shared_access_url && (
                    <a href={client.shared_access_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 text-sm">
                      <Key className="w-4 h-4 text-amber-500" />Accès partagés<ExternalLink className="w-3 h-3 ml-auto" />
                    </a>
                  )}
                  {!client.supports_com_url && !client.shared_access_url && (
                    <p className="text-center text-slate-500 py-4">Aucun lien configuré</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== PROJETS ========== */}
          <TabsContent value="projects" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Projets */}
              <Card className="card lg:col-span-2">
                <CardHeader className="pb-2 p-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Projets ({projects.length})</CardTitle>
                    <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
                      <Link to={`/agency/new-project?client=${clientId}`}><Plus className="w-3 h-3 mr-1" />Nouveau</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {projects.slice(0, 8).map(p => (
                      <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{p.name}</p>
                          <p className="text-xs text-slate-500">{p.budget ? `${p.budget}€` : 'N/A'}</p>
                        </div>
                        <Badge variant="outline" className={cn("text-[10px] ml-2", 
                          p.status === 'completed' && 'bg-emerald-50 text-emerald-700',
                          p.status === 'in_progress' && 'bg-blue-50 text-blue-700',
                          p.status === 'brief_pending' && 'bg-purple-50 text-purple-700'
                        )}>{p.status}</Badge>
                      </Link>
                    ))}
                  </div>
                  {projects.length > 8 && (
                    <Button variant="ghost" className="w-full mt-2 text-xs" asChild>
                      <Link to={`/projects?client=${clientId}`}>Voir tous les projets ({projects.length})<ChevronRight className="w-3 h-3 ml-1" /></Link>
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Tickets */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Tickets ({tickets.length})</CardTitle>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowNewTicket(true)}>
                      <Plus className="w-3 h-3 mr-1" />Nouveau
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {tickets.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Aucun ticket</p>
                  ) : (
                    <div className="space-y-2">
                      {tickets.slice(0, 5).map(t => (
                        <Link key={t.id} to={`/tickets/${t.id}`} className="flex items-center justify-between p-2 rounded hover:bg-slate-50">
                          <p className="text-sm truncate flex-1">{t.title}</p>
                          <Badge variant="outline" className={cn("text-[9px]", 
                            ['closed', 'resolved'].includes(t.status) ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                          )}>{t.status}</Badge>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Contrats */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Contrats ({contracts.length})</CardTitle>
                    <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
                      <Link to={`/contracts/new?client=${clientId}`}><Plus className="w-3 h-3 mr-1" />Nouveau</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {contracts.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Aucun contrat</p>
                  ) : (
                    <div className="space-y-2">
                      {contracts.slice(0, 5).map(c => (
                        <Link key={c.id} to={`/contracts/${c.id}`} className="flex items-center justify-between p-2 rounded hover:bg-slate-50">
                          <p className="text-sm truncate flex-1">{c.title || c.project_name}</p>
                          <Badge variant="outline" className={cn("text-[9px]",
                            c.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                            c.status === 'pending' ? 'bg-amber-50 text-amber-700' : ''
                          )}>{c.status}</Badge>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== FINANCE ========== */}
          <TabsContent value="finance" className="space-y-3 mt-3">
            {/* Graphique CA Evolution */}
            <Card className="card">
              <CardHeader className="pb-2 p-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />Évolution du chiffre d'affaires
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={caChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="caGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${v}€`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                      formatter={(value) => [`${value.toLocaleString('fr-FR')}€`, 'CA']}
                    />
                    <Area type="monotone" dataKey="ca" stroke="#6366f1" strokeWidth={2} fillOpacity={1} fill="url(#caGradient)" animationDuration={1500} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Wallet */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Wallet className="w-4 h-4" />Wallet</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 text-center">
                  <p className="text-4xl font-bold" style={{ color: walletBalance >= 0 ? '#16a34a' : '#dc2626' }}>{walletBalance.toFixed(1)}h</p>
                  <p className="text-xs text-slate-500">≈ {(walletBalance * 90).toFixed(0)}€ HT</p>
                  <Button size="sm" variant="outline" className="mt-3 w-full text-xs" asChild>
                    <Link to={`/clients-wallets?client=${clientId}`}><Plus className="w-3 h-3 mr-1" />Créditer</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Historique Wallet */}
              <Card className="card lg:col-span-2">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm">Historique wallet</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <ScrollArea className="h-[200px]">
                    {wallet?.transactions?.length > 0 ? (
                      <div className="space-y-2">
                        {wallet.transactions.slice().reverse().slice(0, 20).map((tx, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                            <div>
                              <p className="font-medium">{tx.type || tx.description}</p>
                              <p className="text-[10px] text-slate-500">{tx.date ? new Date(tx.date).toLocaleDateString('fr-FR') : ''}</p>
                            </div>
                            <span className={cn("font-bold", tx.hours > 0 ? 'text-emerald-600' : 'text-red-600')}>
                              {tx.hours > 0 ? '+' : ''}{tx.hours}h
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-500 py-8">Aucune transaction</p>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Échéanciers */}
              <Card className="card lg:col-span-3">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4" />Échéanciers</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {schedules.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Aucun échéancier</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {schedules.map(s => (
                        <div key={s.id} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-medium text-sm">{s.title || s.project_name}</p>
                            <Badge variant="outline" className="text-[9px]">{s.total_amount?.toFixed(0)}€</Badge>
                          </div>
                          <div className="flex gap-2">
                            {s.schedule?.slice(0, 4).map((item, i) => (
                              <div key={i} className={cn("flex-1 text-center p-1 rounded text-[10px]", item.paid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
                                {item.paid ? '✓' : `${item.amount}€`}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== DOCUMENTS ========== */}
          <TabsContent value="docs" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Reçus */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><Receipt className="w-4 h-4" />Reçus ({receipts.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {receipts.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Aucun reçu</p>
                  ) : (
                    <ScrollArea className="h-[200px]">
                      <div className="space-y-2">
                        {receipts.map(r => (
                          <div key={r.id} className="flex justify-between items-center p-2 rounded hover:bg-slate-50">
                            <div className="flex items-center gap-2">
                              <Receipt className="w-4 h-4 text-emerald-500" />
                              <div>
                                <p className="text-sm font-medium">{r.receipt_number}</p>
                                <p className="text-[10px] text-slate-500">{new Date(r.created_at).toLocaleDateString('fr-FR')}</p>
                              </div>
                            </div>
                            <span className="font-bold text-sm">{r.amount_ttc?.toFixed(0)}€</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              {/* Contrats */}
              <Card className="card">
                <CardHeader className="pb-2 p-3">
                  <CardTitle className="text-sm flex items-center gap-2"><FileSignature className="w-4 h-4" />Contrats</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {contracts.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Aucun contrat</p>
                  ) : (
                    <ScrollArea className="h-[200px]">
                      <div className="space-y-2">
                        {contracts.map(c => (
                          <Link key={c.id} to={`/contracts/${c.id}`} className="flex justify-between items-center p-2 rounded hover:bg-slate-50">
                            <div className="flex items-center gap-2">
                              <FileSignature className="w-4 h-4 text-indigo-500" />
                              <p className="text-sm truncate max-w-[150px]">{c.title || c.project_name}</p>
                            </div>
                            <Badge variant="outline" className="text-[9px]">{c.status}</Badge>
                          </Link>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* ========== MODALS ========== */}
      
      {/* Edit Client */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle>Modifier le client</DialogTitle></DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <div className="grid grid-cols-2 gap-3 py-4">
              {/* Logo entreprise */}
              <div className="col-span-2 flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-white">
                  {client.company_logo_url ? (
                    <img 
                      src={client.company_logo_url.startsWith('/') 
                        ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${client.company_logo_url}` 
                        : client.company_logo_url
                      }
                      alt="Logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-medium">Logo entreprise</Label>
                  <p className="text-[10px] text-slate-500 mb-2">Le client peut aussi ajouter son logo depuis son espace</p>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const res = await api.post(`/users/${clientId}/company-logo`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setClient(prev => ({ ...prev, company_logo_url: res.data.company_logo_url }));
                          toast.success('Logo mis à jour');
                        } catch (error) {
                          toast.error('Erreur lors de l\'upload');
                        }
                      }}
                    />
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" asChild>
                      <span><Upload className="w-3 h-3 mr-1" />{client.company_logo_url ? 'Changer' : 'Ajouter logo'}</span>
                    </Button>
                  </label>
                  {client.company_logo_url && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs text-red-500 ml-2"
                      onClick={async () => {
                        try {
                          await api.delete(`/users/${clientId}/company-logo`);
                          setClient(prev => ({ ...prev, company_logo_url: null }));
                          toast.success('Logo supprimé');
                        } catch (error) {
                          toast.error('Erreur');
                        }
                      }}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />Supprimer
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="col-span-2"><Label className="text-xs">Entreprise</Label><Input value={editData.company_name || ''} onChange={e => setEditData({...editData, company_name: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">Prénom</Label><Input value={editData.first_name || ''} onChange={e => setEditData({...editData, first_name: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">Nom</Label><Input value={editData.last_name || ''} onChange={e => setEditData({...editData, last_name: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">Fonction</Label><Input value={editData.function || ''} onChange={e => setEditData({...editData, function: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">Email</Label><Input value={editData.email || ''} onChange={e => setEditData({...editData, email: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">Téléphone</Label><Input value={editData.phone || ''} onChange={e => setEditData({...editData, phone: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">SIRET</Label><Input value={editData.siret || ''} onChange={e => setEditData({...editData, siret: e.target.value})} className="h-9" /></div>
              <div className="col-span-2"><Label className="text-xs">Activité / Secteur</Label><Input value={editData.activity_description || ''} onChange={e => setEditData({...editData, activity_description: e.target.value})} className="h-9" placeholder="Ex: Commerce de détail, Restauration..." /></div>
              <div className="col-span-2"><Label className="text-xs">Adresse</Label><Input value={editData.address || ''} onChange={e => setEditData({...editData, address: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">Code postal</Label><Input value={editData.postal_code || ''} onChange={e => setEditData({...editData, postal_code: e.target.value})} className="h-9" /></div>
              <div><Label className="text-xs">Ville</Label><Input value={editData.city || ''} onChange={e => setEditData({...editData, city: e.target.value})} className="h-9" /></div>
              
              <div className="col-span-2 border-t pt-3 mt-2"><h4 className="font-medium text-sm mb-2">Réseaux sociaux</h4></div>
              <div><Label className="text-xs">Site web</Label><Input value={editData.website || ''} onChange={e => setEditData({...editData, website: e.target.value})} className="h-9" placeholder="https://" /></div>
              <div><Label className="text-xs">Facebook</Label><Input value={editData.facebook_url || ''} onChange={e => setEditData({...editData, facebook_url: e.target.value})} className="h-9" placeholder="https://facebook.com/..." /></div>
              <div><Label className="text-xs">Instagram</Label><Input value={editData.instagram_url || ''} onChange={e => setEditData({...editData, instagram_url: e.target.value})} className="h-9" placeholder="https://instagram.com/..." /></div>
              <div><Label className="text-xs">LinkedIn</Label><Input value={editData.linkedin_url || ''} onChange={e => setEditData({...editData, linkedin_url: e.target.value})} className="h-9" placeholder="https://linkedin.com/..." /></div>
              <div><Label className="text-xs">Twitter / X</Label><Input value={editData.twitter_url || ''} onChange={e => setEditData({...editData, twitter_url: e.target.value})} className="h-9" placeholder="https://x.com/..." /></div>
              <div><Label className="text-xs">YouTube</Label><Input value={editData.youtube_url || ''} onChange={e => setEditData({...editData, youtube_url: e.target.value})} className="h-9" placeholder="https://youtube.com/..." /></div>
              
              <div className="col-span-2 border-t pt-3 mt-2"><h4 className="font-medium text-sm mb-2">Situation site web</h4></div>
              <div className="col-span-2"><Label className="text-xs">Qui gère le site ?</Label>
                <Select value={editData.has_website_with_us ? 'yes' : editData.has_website_with_competitor ? 'competitor' : editData.website ? 'unknown' : 'no'} onValueChange={v => setEditData({...editData, has_website_with_us: v === 'yes', has_website_with_competitor: v === 'competitor'})}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">✅ Géré par notre agence</SelectItem>
                    <SelectItem value="competitor">⚠️ Géré par un concurrent</SelectItem>
                    <SelectItem value="unknown">🌐 A un site (prestataire inconnu)</SelectItem>
                    <SelectItem value="no">❌ Pas de site web</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editData.has_website_with_us && (
                <>
                  <div><Label className="text-xs">Hébergeur</Label><Input value={editData.hosting_provider || ''} onChange={e => setEditData({...editData, hosting_provider: e.target.value})} className="h-9" placeholder="OVH, O2Switch, Infomaniak..." /></div>
                  <div><Label className="text-xs">Registrar domaine</Label><Input value={editData.domain_registrar || ''} onChange={e => setEditData({...editData, domain_registrar: e.target.value})} className="h-9" placeholder="OVH, Gandi, Google..." /></div>
                  <div><Label className="text-xs">Date renouvellement</Label><Input type="date" value={editData.website_renewal_date?.slice(0,10) || ''} onChange={e => setEditData({...editData, website_renewal_date: e.target.value})} className="h-9" /></div>
                </>
              )}
              {editData.has_website_with_competitor && (
                <>
                  <div><Label className="text-xs">Nom du concurrent</Label><Input value={editData.competitor_name || ''} onChange={e => setEditData({...editData, competitor_name: e.target.value})} className="h-9" /></div>
                  <div><Label className="text-xs">Fin engagement</Label><Input type="date" value={editData.competitor_contract_end_date?.slice(0,10) || ''} onChange={e => setEditData({...editData, competitor_contract_end_date: e.target.value})} className="h-9" /></div>
                  <div><Label className="text-xs">Montant mensuel (€)</Label><Input type="number" value={editData.competitor_monthly_amount || ''} onChange={e => setEditData({...editData, competitor_monthly_amount: parseFloat(e.target.value)})} className="h-9" /></div>
                </>
              )}
              
              <div className="col-span-2 border-t pt-3 mt-2"><h4 className="font-medium text-sm mb-2">Liens</h4></div>
              <div><Label className="text-xs">Supports de com'</Label><Input value={editData.supports_com_url || ''} onChange={e => setEditData({...editData, supports_com_url: e.target.value})} className="h-9" placeholder="URL vers les supports" /></div>
              <div><Label className="text-xs">Accès partagés</Label><Input value={editData.shared_access_url || ''} onChange={e => setEditData({...editData, shared_access_url: e.target.value})} className="h-9" placeholder="URL vers les accès" /></div>
            </div>
          </ScrollArea>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowEditClient(false)}>Annuler</Button>
            <Button onClick={saveClient} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tags */}
      <Dialog open={showTagsModal} onOpenChange={setShowTagsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Gérer les tags</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2 py-4">
            {AVAILABLE_TAGS.map(tag => (
              <Badge key={tag.id} variant="outline" onClick={() => toggleTag(tag.id)}
                className={cn("cursor-pointer text-xs px-3 py-1.5", tag.color, clientTags.includes(tag.id) && 'ring-2 ring-offset-1')}>
                {clientTags.includes(tag.id) && <CheckCircle className="w-3 h-3 mr-1" />}{tag.label}
              </Badge>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => { saveClient(); setShowTagsModal(false); }}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Contact */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingContact ? 'Modifier le contact' : 'Ajouter un contact'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            <div><Label className="text-xs">Prénom</Label><Input value={contactForm.first_name} onChange={e => setContactForm({...contactForm, first_name: e.target.value})} className="h-9" /></div>
            <div><Label className="text-xs">Nom</Label><Input value={contactForm.last_name} onChange={e => setContactForm({...contactForm, last_name: e.target.value})} className="h-9" /></div>
            <div className="col-span-2"><Label className="text-xs">Fonction</Label><Input value={contactForm.function} onChange={e => setContactForm({...contactForm, function: e.target.value})} className="h-9" /></div>
            <div><Label className="text-xs">Email</Label><Input value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} className="h-9" /></div>
            <div><Label className="text-xs">Téléphone</Label><Input value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} className="h-9" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContact(false)}>Annuler</Button>
            <Button onClick={saveContact} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{editingContact ? 'Modifier' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Ticket */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><TicketIcon className="w-5 h-5 text-indigo-500" />Créer un ticket support</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <div><Label className="text-xs">Sujet *</Label><Input value={ticketForm.subject} onChange={e => setTicketForm({...ticketForm, subject: e.target.value})} className="h-9" placeholder="Ex: Problème avec..." /></div>
            <div><Label className="text-xs">Projet associé</Label>
              <Select value={ticketForm.project_id || "none"} onValueChange={v => setTicketForm({...ticketForm, project_id: v === "none" ? "" : v})}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Aucun projet spécifique" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun projet</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Priorité</Label>
              <Select value={ticketForm.priority} onValueChange={v => setTicketForm({...ticketForm, priority: v})}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">🟢 Basse</SelectItem>
                  <SelectItem value="normal">🟡 Normale</SelectItem>
                  <SelectItem value="high">🔴 Haute</SelectItem>
                  <SelectItem value="urgent">🚨 Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Message *</Label><Textarea value={ticketForm.message} onChange={e => setTicketForm({...ticketForm, message: e.target.value})} rows={4} placeholder="Décrivez votre demande..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTicket(false)}>Annuler</Button>
            <Button onClick={createTicket} disabled={saving || !ticketForm.subject || !ticketForm.message}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Créer le ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exchange */}
      <Dialog open={showExchangeModal} onOpenChange={setShowExchangeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Saisir un échange</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <div><Label className="text-xs">Type</Label>
              <Select value={exchangeForm.type} onValueChange={v => setExchangeForm({...exchangeForm, type: v})}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">📞 Appel</SelectItem>
                  <SelectItem value="email">📧 Email</SelectItem>
                  <SelectItem value="meeting">🎥 Réunion</SelectItem>
                  <SelectItem value="visit">🏢 Visite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Date</Label><Input type="datetime-local" value={exchangeForm.date} onChange={e => setExchangeForm({...exchangeForm, date: e.target.value})} className="h-9" /></div>
            <div><Label className="text-xs">Sujet</Label><Input value={exchangeForm.subject} onChange={e => setExchangeForm({...exchangeForm, subject: e.target.value})} className="h-9" /></div>
            <div><Label className="text-xs">Résumé</Label><Textarea value={exchangeForm.content} onChange={e => setExchangeForm({...exchangeForm, content: e.target.value})} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExchangeModal(false)}>Annuler</Button>
            <Button onClick={saveExchange} disabled={saving || !exchangeForm.subject}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetailPageV2;
